// ─── <ProductVideo/> ─────────────────────────────────────────────────────────
// Renders an inline video player for a product / kit when admin has set an
// optional videoUrl. Supports:
//   • YouTube (youtube.com/watch?v=, youtu.be/, youtube.com/shorts/, embed/)
//   • Vimeo   (vimeo.com/NNN, player.vimeo.com/video/NNN)
//   • Direct video files (.mp4, .webm, .mov, .m4v, .ogg)
//   • Anything else — rendered as a raw iframe (works for pre-embed URLs)
//
// Fullscreen uses the browser's native Fullscreen API on the container so the
// SAME iframe / video element expands — no duplicate playback, no state loss.
// Falls back to a modal overlay if the browser blocks fullscreen.
//
// Returns null if `url` is empty — admin leaving the field blank means the
// section stays hidden entirely.

import React, { useState, useRef } from 'react';

/** Parse a user-pasted URL into an embeddable form.
 *  Returns { kind, embedUrl } or null for empty input. */
export function parseVideoUrl(url) {
  if (!url) return null;
  const s = String(url).trim();
  if (!s) return null;
  // YouTube (short, long, shorts, embed, nocookie)
  let m = s.match(/(?:youtube(?:-nocookie)?\.com\/(?:watch\?v=|embed\/|shorts\/|v\/)|youtu\.be\/)([\w-]{6,})/i);
  if (m) return { kind: 'youtube', embedUrl: `https://www.youtube.com/embed/${m[1]}?rel=0&modestbranding=1` };
  // Vimeo
  m = s.match(/vimeo\.com\/(?:video\/|channels\/[^/]+\/)?(\d+)/i);
  if (m) return { kind: 'vimeo', embedUrl: `https://player.vimeo.com/video/${m[1]}` };
  // Direct file
  if (/\.(mp4|webm|mov|m4v|ogg|ogv)(\?|#|$)/i.test(s)) return { kind: 'file', embedUrl: s };
  // Unknown — assume the admin pasted an already-embeddable URL (vendor LMS,
  // Wistia, Loom, etc). Iframe it as-is.
  return { kind: 'iframe', embedUrl: s };
}

export default function ProductVideo({ url, title = "Product video", accent = "#0B1F3A" }) {
  const containerRef = useRef(null);
  const [modalFs, setModalFs] = useState(false);
  const parsed = parseVideoUrl(url);
  if (!parsed) return null;

  const toggleFullscreen = () => {
    const el = containerRef.current;
    if (!el) return;
    // Native fullscreen first — keeps the same playing element.
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) {
      try { req.call(el); return; } catch(e) { /* fall through */ }
    }
    // Fallback: CSS-based fullscreen modal (covers viewport).
    setModalFs(true);
  };

  const player = parsed.kind === 'file'
    ? <video src={parsed.embedUrl} controls playsInline style={{ width: '100%', height: '100%', background: '#000', display: 'block' }}/>
    : <iframe title={title} src={parsed.embedUrl}
        style={{ width: '100%', height: '100%', border: 'none', background: '#000', display: 'block' }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen/>;

  const fsBtnStyle = {
    position: 'absolute', top: 10, right: 10,
    background: 'rgba(0,0,0,0.55)', color: '#fff',
    border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6,
    padding: '6px 11px', fontSize: 12, fontWeight: 500, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 5,
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)'
  };

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'relative', width: '100%', aspectRatio: '16 / 9',
          borderRadius: 12, overflow: 'hidden', background: '#000',
          border: `1px solid ${accent}22`
        }}
      >
        {player}
        <button
          type="button"
          onClick={toggleFullscreen}
          style={fsBtnStyle}
          title="Watch fullscreen"
          aria-label="Watch fullscreen"
        >
          <span style={{ fontSize: 14, lineHeight: 1 }}>⛶</span>
          <span>Fullscreen</span>
        </button>
      </div>
      {modalFs && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.96)',
            zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setModalFs(false); }}
        >
          <button
            type="button"
            onClick={() => setModalFs(false)}
            style={{
              position: 'absolute', top: 16, right: 16,
              background: 'rgba(255,255,255,0.12)', color: '#fff',
              border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8,
              padding: '8px 14px', fontSize: 13, cursor: 'pointer', zIndex: 10
            }}
          >✕ Close</button>
          <div style={{ width: '100%', maxWidth: 1400, aspectRatio: '16 / 9' }}>
            {/* New element in the modal — this is the fallback path. The inline
                player stops because the iframe/video behind it gets visually
                hidden. Re-mounting in the modal means a fresh load. */}
            {player}
          </div>
        </div>
      )}
    </>
  );
}
